import { TypedDocumentNode, gql } from '@apollo/client';
import { faArrowDownLeft } from '@fortawesome/pro-solid-svg-icons';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Currency, UserAccountEntryEntry } from '__generated__/globalTypes';
import { FontAwesomeIcon } from 'atoms/icons';
import { glossary } from 'lib/glossary';
import { type OptFragmentOf, unmaskFragment } from 'lib/graphql';

import { AccountEntry } from '../AccountEntry';
import type { Deposit_userAccountEntry } from './__generated__/index.graphql';

const DepositIcon = styled(FontAwesomeIcon)`
  width: var(--double-unit);
  height: var(--double-unit);
  border-radius: 50%;
  align-self: center;
  background-color: transparent;
  border: 1px solid var(--c-onBase-success);
  color: var(--c-onBase-success);
  padding: var(--unit);
`;

type Props = {
  userAccountEntry: OptFragmentOf<Deposit_userAccountEntry>;
  withIcon?: boolean;
  expandable?: boolean;
  primaryCurrency: Currency;
};

export const Deposit = ({
  userAccountEntry: userAccountEntry_,
  withIcon = false,
  expandable,
  primaryCurrency,
}: Props) => {
  const userAccountEntry = unmaskFragment(userAccountEntry_);
  const { formatMessage } = useIntl();

  if (userAccountEntry.entryType !== UserAccountEntryEntry.DEPOSIT) return null;

  return (
    <AccountEntry
      userAccountEntry={userAccountEntry}
      title={formatMessage(glossary.deposit)}
      expandable={expandable}
      icon={withIcon ? <DepositIcon icon={faArrowDownLeft} /> : undefined}
      primaryCurrency={primaryCurrency}
    />
  );
};

Deposit.fragments = {
  userAccountEntry: gql`
    fragment Deposit_userAccountEntry on UserAccountEntry {
      id
      entryType
      ...AccountEntry_userAccountEntry
    }
    ${AccountEntry.fragments.userAccountEntry}
  ` as TypedDocumentNode<Deposit_userAccountEntry>,
};
